<template>
  <div class="col-sm-12">
    <div class="card shadow-sm report-page">
      <!-- Filters Section -->
      <div class="dropdown-container">
        <!-- Distributor Dropdown -->
        <div class="dropdown-item">
          <label for="distributor">Select Distributor</label>
          <select
            v-model="filters.distributor"
            id="distributor"
            @change="onDistributorChange"
            :disabled="isDistributorLocked || loading"
          >
            <option v-if="!isDistributorLocked" value="">Select Distributor</option>
            <option
              v-for="dist in distributors"
              :key="dist.profile_id ?? dist.id"
              :value="String(dist.profile_id ?? dist.id)"
            >
              {{ dist.name ?? dist.first_name ?? dist.full_name ?? 'Unknown' }}
            </option>
          </select>
        </div>

        <div class="search-container">
          <button type="button" class="btn button-default" :disabled="loading" @click="search">
            <i v-if="loading" class="fa fa-spinner fa-spin me-1"></i>
            {{ loading ? "Searching..." : "Search" }}
          </button>
          <button type="button" class="btn button-export"
            :disabled="!filters.distributor || !reportData.length || loading" @click="exportToExcel">
            Export to Excel
          </button>
        </div>
      </div>

      <!-- Loading Widget -->
      <div v-if="loading" class="loading-container mt-4">
        <div class="loading-card">
          <div class="loading-spinner-wrapper">
            <i class="fa fa-spinner fa-spin loading-icon"></i>
          </div>
          <h6 class="loading-title">
            <span>Loading Distributor Stock Report...</span>
          </h6>
          <p class="loading-subtitle">Please wait while the records are being retrieved.</p>
        </div>
      </div>

      <!-- Single Summary Card Split to Qty and Volume -->
      <div v-if="!loading && reportData.length > 0" class="single-summary-card mt-3 mb-4">
        <div class="row g-0">
          <!-- QUANTITY SECTION (Left) -->
          <div class="col-md-6 summary-split-col qty-split-col p-3">
            <div class="split-col-header d-flex justify-content-between align-items-center mb-3">
              <h6 class="split-col-title mb-0">
                <i class="fa fa-cubes me-2 text-primary"></i>QUANTITY (QTY)
              </h6>
              <div class="section-total-badge">
                <span class="total-label text-muted">Total: </span>
                <strong class="total-val text-primary font-monospace">{{ formatNumber(totalNormalQty + totalDamagedQty) }}</strong>
              </div>
            </div>
            <div class="row g-3">
              <div class="col-6">
                <div class="tile tile-sales">
                  <h5>Total Normal</h5>
                  <p>{{ formatNumber(totalNormalQty) }}</p>
                </div>
              </div>
              <div class="col-6">
                <div class="tile tile-loss">
                  <h5>Total Damaged</h5>
                  <p>{{ formatNumber(totalDamagedQty) }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- VOLUME SECTION (Right) -->
          <div class="col-md-6 summary-split-col vol-split-col p-3">
            <div class="split-col-header d-flex justify-content-between align-items-center mb-3">
              <h6 class="split-col-title mb-0">
                <i class="fa fa-balance-scale me-2 text-primary"></i>VOLUME (MT)
              </h6>
              <div class="section-total-badge">
                <span class="total-label text-muted">Total: </span>
                <strong class="total-val text-primary font-monospace">{{ formatVolume(totalVolume + totalDamagedVolume) }} MT</strong>
              </div>
            </div>
            <div class="row g-3">
              <div class="col-6">
                <div class="tile tile-sales">
                  <h5>Total Normal</h5>
                  <p>{{ formatVolume(totalVolume) }}</p>
                </div>
              </div>
              <div class="col-6">
                <div class="tile tile-loss">
                  <h5>Total Damaged</h5>
                  <p>{{ formatVolume(totalDamagedVolume) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Results Table Split to Qty and Volume -->
      <div v-if="!loading && reportData.length > 0" class="table-container">
        <table class="table table-responsive-sm">
          <thead>
            <tr>
              <th rowspan="2" class="align-middle text-start">ITEM</th>
              <th colspan="2" class="text-center group-header">QUANTITY</th>
              <th colspan="2" class="text-center group-header split-col">VOLUME (MT)</th>
            </tr>
            <tr>
              <th class="text-end">NORMAL QTY</th>
              <th class="text-end">DAMAGED QTY</th>
              <th class="text-end split-col">NORMAL VOLUME</th>
              <th class="text-end">DAMAGED VOLUME</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in reportData" :key="item.item_name">
              <td class="text-start">{{ item.item_name }}</td>
              <td class="text-end">{{ formatNumber(item.normal_qty) }}</td>
              <td class="text-end" :class="{ 'text-danger fw-bold': Number(item.damaged_qty) > 0 }">
                {{ formatNumber(item.damaged_qty) }}
              </td>
              <td class="text-end split-col">{{ formatVolume(item.total_volume) }}</td>
              <td class="text-end" :class="{ 'text-danger fw-bold': Number(item.total_damaged_volume) > 0 }">
                {{ formatVolume(item.total_damaged_volume) }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td class="text-start"><strong>TOTAL:</strong></td>
              <td class="text-end"><strong>{{ formatNumber(totalNormalQty) }}</strong></td>
              <td class="text-end" :class="{ 'text-danger': Number(totalDamagedQty) > 0 }">
                <strong>{{ formatNumber(totalDamagedQty) }}</strong>
              </td>
              <td class="text-end split-col"><strong>{{ formatVolume(totalVolume) }}</strong></td>
              <td class="text-end" :class="{ 'text-danger': Number(totalDamagedVolume) > 0 }">
                <strong>{{ formatVolume(totalDamagedVolume) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Empty State Widget -->
      <div v-else-if="!loading && searched && reportData.length === 0" class="no-data-container mt-4">
        <div class="no-data-card">
          <div class="no-data-icon-wrapper">
            <i class="fa fa-folder-open-o no-data-icon"></i>
          </div>
          <h5 class="no-data-title">No Data Found</h5>
          <p class="no-data-text">
            There are no stock records matching your selected distributor.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { useToast } from "vue-toastification";
import { fetchdistributorstocks, fetchDistributorsList } from "@/services/dataService";

export default {
  name: "DistributorStockForm",
  data() {
    return {
      profile_id: localStorage.getItem("profile_id") || null,
      userLabel: "",
      roleKey: "OTHER",
      isDistributorLocked: false,
      searched: false,
      filters: {
        distributor: "",
      },
      distributors: [],
      reportData: [],
      loading: false,
      totalNormalQty: 0,
      totalDamagedQty: 0,
      totalVolume: 0,
      totalDamagedVolume: 0,
    };
  },
  created() {
    this.initRoleFromStorage();
    this.loadDistributors();
  },
  methods: {
    initRoleFromStorage() {
      const labelFromStorage =
        localStorage.getItem("user_label") ||
        localStorage.getItem("userLabel") ||
        localStorage.getItem("role") ||
        "";

      this.userLabel = String(labelFromStorage);
      const labelUpper = String(this.userLabel || "").toUpperCase().trim();

      if (
        labelUpper === "DISTRIBUTOR" ||
        labelUpper.startsWith("DISTRIBUTOR") ||
        labelUpper === "DIS" ||
        labelUpper.startsWith("DIS_")
      ) {
        this.roleKey = "DIS";
      } else if (labelUpper === "ASM" || labelUpper.startsWith("ASM_")) {
        this.roleKey = "ASM";
      } else if (labelUpper === "FSM" || labelUpper.startsWith("FSM_")) {
        this.roleKey = "FSM";
      } else {
        this.roleKey = "OTHER";
      }
    },

    isDistributorUser() {
      return this.roleKey === "DIS";
    },

    async loadDistributors() {
      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchDistributorsList(this.profile_id, token);
        this.distributors = response?.data?.data || response?.data || [];

        // If distributor login: auto fill distributor and freeze dropdown
        if (this.isDistributorUser() && this.profile_id) {
          const matched =
            this.distributors.find(
              (d) => Number(d.profile_id ?? d.id) === Number(this.profile_id)
            ) || this.distributors[0];

          this.filters.distributor = String(matched?.profile_id ?? matched?.id ?? "");
          this.isDistributorLocked = true;

          // Auto-load data for distributor
          await this.search();
        }
      } catch (error) {
        console.error("Error fetching distributors:", error);
      }
    },

    onDistributorChange() {
      this.reportData = [];
      this.searched = false;
    },

    async search() {
      this.loading = true;
      this.searched = true;
      const params = {
        distributor_id: this.filters.distributor,
      };

      try {
        const token = localStorage.getItem("token_set");
        const response = await fetchdistributorstocks(params, token);
        this.reportData = response?.data?.data || [];

        // Calculate totalQty and totalVolume
        this.totalNormalQty = this.reportData.reduce(
          (sum, item) => sum + parseFloat(item.normal_qty || 0),
          0
        );
        this.totalDamagedQty = this.reportData.reduce(
          (sum, item) => sum + parseFloat(item.damaged_qty || 0),
          0
        );
        this.totalVolume = this.reportData.reduce(
          (sum, item) => sum + parseFloat(item.total_volume || 0),
          0
        );
        this.totalDamagedVolume = this.reportData.reduce(
          (sum, item) => sum + parseFloat(item.total_damaged_volume || 0),
          0
        );
      } catch (error) {
        console.error("Error fetching report:", error);
      } finally {
        this.loading = false;
      }
    },

    formatNumber(v) {
      if (v === null || v === undefined || v === "") return "0";
      const n = Number(v);
      if (isNaN(n)) return "0";
      return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
    },

    formatVolume(volume) {
      if (!volume) return "0.00";
      const mt = parseFloat(volume) / 1000; // Convert kg to MT
      if (isNaN(mt)) return "0.00";
      return mt.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    toast(type, message) {
      const t = useToast();
      type === "error"
        ? t.error(message, { position: "top-right", timeout: 3000 })
        : t.success(message, { position: "top-right", timeout: 3000 });
    },

    exportToExcel() {
      if (!this.reportData || !this.reportData.length) {
        this.toast("error", "No data available to export.");
        return;
      }

      try {
        const selectedDist = this.distributors.find(
          (d) => String(d.profile_id ?? d.id) === String(this.filters.distributor)
        );
        const distributorName = selectedDist
          ? (selectedDist.name ?? selectedDist.first_name ?? selectedDist.full_name ?? "Unknown")
          : "All Distributors";

        const firstName = localStorage.getItem("first_name") || "";
        const lastName = localStorage.getItem("last_name") || "";
        const userFullName = (firstName + " " + lastName).trim() || "-";
        const generatedAt = new Date().toLocaleString("en-US", { hour12: false });

        // 1. Metadata / Filter rows
        const filterRows = [
          ["Distributor Stock Report"],
          [],
          ["Distributor", distributorName],
          ["Generated At", generatedAt],
          ["Generated By", userFullName],
          [],
        ];

        // 2. Table Column Headers
        const headerRow = [
          "ITEM",
          "NORMAL QTY",
          "DAMAGED QTY",
          "NORMAL VOLUME (MT)",
          "DAMAGED VOLUME (MT)",
        ];

        // 3. Data Rows
        const dataRows = this.reportData.map((item) => [
          item.item_name || "-",
          Number(item.normal_qty || 0),
          Number(item.damaged_qty || 0),
          Number((parseFloat(item.total_volume || 0) / 1000).toFixed(2)),
          Number((parseFloat(item.total_damaged_volume || 0) / 1000).toFixed(2)),
        ]);

        // 4. Totals Row
        const totalsRow = [
          "TOTAL",
          Number(this.totalNormalQty || 0),
          Number(this.totalDamagedQty || 0),
          Number((parseFloat(this.totalVolume || 0) / 1000).toFixed(2)),
          Number((parseFloat(this.totalDamagedVolume || 0) / 1000).toFixed(2)),
        ];

        // 5. Build Final Array of Arrays
        const aoa = [
          ...filterRows,
          headerRow,
          ...dataRows,
          totalsRow,
        ];

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        // Column widths
        ws["!cols"] = [
          { wch: 35 }, // ITEM
          { wch: 18 }, // NORMAL QTY
          { wch: 18 }, // DAMAGED QTY
          { wch: 24 }, // NORMAL VOLUME (MT)
          { wch: 24 }, // DAMAGED VOLUME (MT)
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Distributor_Stock");

        const safeDistName = distributorName.replace(/[^a-zA-Z0-9_-]/g, "_");
        const dateStr = new Date().toISOString().split("T")[0];
        XLSX.writeFile(wb, `Distributor_Stock_Report_${safeDistName}_${dateStr}.xlsx`);

        this.toast("success", "Stock report exported successfully.");
      } catch (error) {
        console.error("Export error:", error);
        this.toast("error", "Failed to export report to Excel.");
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 10px;
}

/* Filters and Actions Layout matching dayreport_form.vue */
.dropdown-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 20px;
  align-items: end;
}

.dropdown-item {
  width: 100%;
}

.dropdown-item.action-item {
  display: flex;
  align-items: flex-end;
}

.dropdown-item.action-item .btn {
  width: 100%;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 13px;
  color: #333;
  white-space: nowrap;
}

select {
  width: 100%;
  height: 40px;
  padding: 8px 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  box-sizing: border-box;
  background-color: #fff;
}

select:focus {
  border-color: #007bff;
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

select:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* Button styles matching dayreport_form.vue */
.btn {
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 5px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: background-color 0.2s ease, opacity 0.2s ease;
}

.btn.button-default {
  background-color: #007bff;
  color: #fff;
}

.btn.button-default:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn.button-export {
  background-color: #28a745;
  color: #fff;
}

.btn.button-export:hover:not(:disabled) {
  background-color: #218838;
}

.btn:disabled {
  background-color: #6c757d !important;
  color: #fff !important;
  cursor: not-allowed;
  opacity: 0.7;
}

@media (min-width: 576px) and (max-width: 1023px) {
  .dropdown-container {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

@media (min-width: 1024px) {
  .dropdown-container {
    grid-template-columns: minmax(220px, 320px) auto auto;
    gap: 10px;
    align-items: end;
    justify-content: start;
  }

  .dropdown-item {
    width: auto;
  }

  .dropdown-item.action-item .btn {
    width: auto;
  }
}

/* =========================================================
   SINGLE SUMMARY CARD (SPLIT TO QTY & VOLUME)
========================================================= */
.single-summary-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.summary-split-col {
  box-sizing: border-box;
}

.vol-split-col {
  border-top: 1px solid #e2e8f0;
}

@media (min-width: 768px) {
  .vol-split-col {
    border-top: none;
    border-left: 1px solid #e2e8f0;
  }
}

.split-col-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #475569;
  margin: 0;
  text-transform: uppercase;
}

.section-total-badge {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.section-total-badge .total-label {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.section-total-badge .total-val {
  font-size: 14px;
  font-weight: 700;
}

/* Tile styling matching system */
.tile {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 12px 14px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  width: 100%;
  box-sizing: border-box;
}

.tile:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12);
}

.tile h5 {
  margin: 0 0 6px 0;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
}

.tile p {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.tile-sales {
  background-color: #e0f7e0; /* Light green system style */
  border: 1px solid #c3e6cb;
}

.tile-loss {
  background-color: #ffcccc; /* Light red system style */
  border: 1px solid #f5c6cb;
}

/* =========================================================
   TABLE LAYOUT MATCHING dayreport_form.vue
========================================================= */
.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top: 20px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #ddd;
  position: relative;
  z-index: 0;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: auto;
  margin-bottom: 0;
}

.table th,
.table td {
  padding: 10px 20px;
  border-bottom: 1px solid #ddd;
  border-top: none;
  border-left: none !important;
  border-right: none !important;
  white-space: nowrap;
  vertical-align: middle;
  min-width: 100px;
  font-size: 0.85rem;
  box-sizing: border-box;
}

.table thead th {
  background-color: #f8f9fa !important;
  color: #333 !important;
  position: sticky;
  font-weight: bold;
  text-transform: uppercase;
  border-bottom: 1px solid #ddd !important;
  border-top: none !important;
  box-shadow: none !important;
}

/* Row 1 sticky header: QUANTITY & VOLUME (MT) */
.table thead tr:nth-child(1) th {
  top: 0;
  z-index: 6;
  height: 42px;
}

/* Row 2 sticky header: NORMAL QTY, DAMAGED QTY, etc. positioned beneath row 1 */
.table thead tr:nth-child(2) th {
  top: 42px;
  z-index: 5;
  height: 42px;
  border-bottom: 2px solid #ddd !important;
}

/* Item column spanning both header rows */
.table thead tr:nth-child(1) th[rowspan="2"] {
  top: 0;
  z-index: 7;
  vertical-align: middle;
  border-bottom: 2px solid #ddd !important;
}

.group-header {
  letter-spacing: 0.5px;
}

.split-col {
  border-left: 1px solid #ddd !important;
}

.table tbody tr:hover {
  background-color: #f9f9f9;
}

.table tfoot {
  font-weight: bold;
  background-color: #f1f1f1 !important;
}

.table tfoot td {
  background-color: #f1f1f1 !important;
  border-top: 1px solid #ddd;
  border-bottom: none;
  font-weight: bold;
}

/* =========================================================
   LOADING STATE MATCHING dayreport_form.vue
========================================================= */
.loading-container {
  width: 100%;
}

.loading-card {
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 42px 24px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.loading-spinner-wrapper {
  margin-bottom: 12px;
}

.loading-icon {
  font-size: 32px;
  color: #007bff;
}

.loading-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* =========================================================
   EMPTY STATE MATCHING dayreport_form.vue
========================================================= */
.no-data-container {
  width: 100%;
  margin-top: 24px;
}

.no-data-card {
  text-align: center;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 48px 32px;
  width: 100%;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;
}

.no-data-card:hover {
  border-color: #94a3b8;
  background: #f1f5f9;
}

.no-data-icon-wrapper {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 18px auto;
  color: #64748b;
  font-size: 32px;
  transition: transform 0.2s ease;
}

.no-data-card:hover .no-data-icon-wrapper {
  transform: scale(1.05);
  background: #dbeafe;
  color: #2563eb;
}

.no-data-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-text {
  font-size: 14px;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto 16px auto;
  line-height: 1.5;
}
</style>